import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";

const WarehouseManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [warehouseTypes, setWarehouseTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    WarehouseID: "",
    WarehouseName: "",
    WarehouseType: "",
    AddressNo: "",
    AddressMoo: "",
    AddressRoad: "",
    AddressSubdistrict: "",
    AddressDistrict: "",
    Province: "",
    PostalCode: "",
    Latitude: "",
    Longitude: "",
    Remark: "",
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // Check if user has permission to edit/add (OfficeType === 'สทช.')
  const hasEditPermission = profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสคลัง",
      selector: (row) => row.WarehouseID,
      sortable: true,
    },
    {
      name: "ชื่อคลัง",
      selector: (row) => row.WarehouseName,
      sortable: true,
    },
    {
      name: "ประเภทคลัง",
      selector: (row) => {
        const type = warehouseTypes.find((t) => t.TypeID === row.WarehouseType);
        return type ? type.TypeName : row.WarehouseType || "-";
      },
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) =>
        hasEditPermission ? (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="แก้ไข"
          />
        ) : (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="รายละเอียด"
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchWarehouseTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehouseTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouseTypes(response.TypeList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouse types:", error);
    }
  };

  const fetchWarehouses = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineWarehousesList || [];
        setWarehouses(data);
        setFilteredWarehouses(data);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
      fetchWarehouseTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterType, filterStatus);
  };

  const handleTypeFilter = (e) => {
    const value = e.target.value;
    setFilterType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterType, value);
  };

  const applyFilters = (search, type, status) => {
    let filtered = warehouses;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const typeName =
          warehouseTypes.find((t) => t.TypeID === row.WarehouseType)?.TypeName ||
          row.WarehouseType ||
          "";
        return searchTerms.every((term) =>
          [
            row.WarehouseID.toLowerCase(),
            row.WarehouseName.toLowerCase(),
            typeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (type) {
      filtered = filtered.filter((row) => row.WarehouseType === type);
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredWarehouses(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterStatus("");
    setFilteredWarehouses(warehouses);
  };

  const handleAddNew = () => {
    setFormData({
      WarehouseID: "",
      WarehouseName: "",
      WarehouseType: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Latitude: row.Latitude ? row.Latitude.toString() : "",
      Longitude: row.Longitude ? row.Longitude.toString() : "",
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "WarehouseID":
      case "WarehouseName":
      case "WarehouseType":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "PostalCode":
        if (value && !/^\d{5}$/.test(value)) {
          newErrors[name] = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
        } else {
          delete newErrors[name];
        }
        break;
      case "Latitude":
        if (value && (isNaN(value) || value < -90 || value > 90)) {
          newErrors[name] = "ละติจูดต้องเป็นตัวเลขระหว่าง -90 ถึง 90";
        } else {
          delete newErrors[name];
        }
        break;
      case "Longitude":
        if (value && (isNaN(value) || value < -180 || value > 180)) {
          newErrors[name] = "ลองจิจูดต้องเป็นตัวเลขระหว่าง -180 ถึง 180";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.WarehouseID &&
      formData.WarehouseName &&
      formData.WarehouseType &&
      (!formData.PostalCode || /^\d{5}$/.test(formData.PostalCode)) &&
      (!formData.Latitude ||
        (!isNaN(formData.Latitude) &&
          formData.Latitude >= -90 &&
          formData.Latitude <= 90)) &&
      (!formData.Longitude ||
        (!isNaN(formData.Longitude) &&
          formData.Longitude >= -180 &&
          formData.Longitude <= 180)) &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateVaccineWarehousesByID"
        : "/createVaccineWarehouses";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Latitude: formData.Latitude ? parseFloat(formData.Latitude) : null,
          Longitude: formData.Longitude ? parseFloat(formData.Longitude) : null,
          Status: formData.Status === "1",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตคลังสำเร็จ!" : "สร้างคลังสำเร็จ!");
        setShowForm(false);
        fetchWarehouses();
      }
    } catch (error) {
      console.error("Error saving warehouse:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      WarehouseID: "",
      WarehouseName: "",
      WarehouseType: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
    });
    setErrors({});
  };

  const handleDelete = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการลบข้อมูล");
      return;
    }
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบคลังนี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineWarehousesByID",
        data: {
          ...formData,
          Status: false,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ลบคลังสำเร็จ!");
        setShowForm(false);
        fetchWarehouses();
      }
    } catch (error) {
      console.error("Error deleting warehouse:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลคลังวัคซีน
            </Heading>
          </HStack>
        </Box>

        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลคลัง" : "เพิ่มข้อมูลใหม่"}
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseID}
                  isReadOnly={!hasEditPermission}
                  isDisabled={isEditing && hasEditPermission}
                >
                  <FormLabel>รหัสคลัง</FormLabel>
                  <Input
                    name="WarehouseID"
                    value={formData.WarehouseID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.WarehouseID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseName}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ชื่อคลัง</FormLabel>
                  <Input
                    name="WarehouseName"
                    value={formData.WarehouseName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.WarehouseName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseType}
                  isDisabled={!hasEditPermission}
                >
                  <FormLabel>ประเภทคลัง</FormLabel>
                  <Select
                    name="WarehouseType"
                    value={formData.WarehouseType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภท"
                    colorScheme="iBlue"
                  >
                    {warehouseTypes.map((type) => (
                      <option key={type.TypeID} value={type.TypeID}>
                        {type.TypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.WarehouseType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>เลขที่อยู่</FormLabel>
                  <Input
                    name="AddressNo"
                    value={formData.AddressNo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมู่ที่</FormLabel>
                  <Input
                    name="AddressMoo"
                    value={formData.AddressMoo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ถนน</FormLabel>
                  <Input
                    name="AddressRoad"
                    value={formData.AddressRoad}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำบล</FormLabel>
                  <Input
                    name="AddressSubdistrict"
                    value={formData.AddressSubdistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>อำเภอ</FormLabel>
                  <Input
                    name="AddressDistrict"
                    value={formData.AddressDistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>จังหวัด</FormLabel>
                  <Input
                    name="Province"
                    value={formData.Province}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isInvalid={!!errors.PostalCode}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>รหัสไปรษณีย์</FormLabel>
                  <Input
                    name="PostalCode"
                    value={formData.PostalCode}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.PostalCode}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isInvalid={!!errors.Latitude}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ละติจูด</FormLabel>
                  <Input
                    name="Latitude"
                    value={formData.Latitude}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Latitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isInvalid={!!errors.Longitude}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ลองจิจูด</FormLabel>
                  <Input
                    name="Longitude"
                    value={formData.Longitude}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Longitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}></GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isDisabled={!hasEditPermission}>
                  <FormLabel>สถานะ</FormLabel>
                  <Switch
                    name="Status"
                    isChecked={formData.Status === "1"}
                    onChange={handleStatusChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {hasEditPermission && isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              {hasEditPermission && (
                <IconButton.Save onClick={handleSave} isLoading={isLoading} />
              )}
            </HStack>
          </Box>
        )}

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาคลัง..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterType}
              onChange={handleTypeFilter}
              placeholder="เลือกประเภทคลัง"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {warehouseTypes.map((type) => (
                <option key={type.TypeID} value={type.TypeID}>
                  {type.TypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          {hasEditPermission && (
            <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลคลัง" />
          )}
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : warehouses.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลคลังวัคซีน
          </Text>
        ) : filteredWarehouses.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredWarehouses}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default WarehouseManagement;